'use client';

import { createContext, useContext, ReactNode } from 'react';
import { useSession } from '@/lib/auth-client';
import {
  useCollections as useApiCollections,
  useCreateCollection,
  useUpdateCollection,
  useDeleteCollection,
  useAddItemToCollection,
  useRemoveItemFromCollection,
} from '@/lib/hooks/useCollections';
import {
  transformVisibilityToIsPublic,
  transformCollectionToCreateRequest,
  transformCollectionToUpdateRequest,
} from '@/lib/utils/data-transforms';

export interface Collection {
  id: string;
  name: string;
  description?: string;
  image?: string | null;
  tags?: string[];
  visibility: 'public' | 'private';
  productIds: string[];
  createdAt: Date;
}

interface CollectionsContextType {
  collections: Collection[];
  createCollection: (
    name: string,
    visibility: 'public' | 'private'
  ) => Promise<Collection | undefined>;
  deleteCollection: (id: string) => void;
  addToCollection: (collectionId: string, productId: string) => void;
  removeFromCollection: (collectionId: string, productId: string) => void;
  updateCollection: (
    id: string,
    updates: Partial<Pick<Collection, 'name' | 'visibility' | 'description' | 'image'>> & {
      tags?: string[];
    }
  ) => void;
  // Additional API-specific states
  isLoading?: boolean;
  error?: Error | null;
}

const CollectionsContext = createContext<CollectionsContextType | null>(null);

export function CollectionsProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending: isSessionPending } = useSession();

  // API hooks
  const { data: apiCollectionsData, isLoading, error } = useApiCollections();
  const createMutation = useCreateCollection();
  const updateMutation = useUpdateCollection();
  const deleteMutation = useDeleteCollection();
  const addItemMutation = useAddItemToCollection();
  const removeItemMutation = useRemoveItemFromCollection();

  // Flatten collections from infinite query pages
  const apiCollections = apiCollectionsData?.pages.flatMap((page) => page.collections) || [];

  // Transform API collections to localStorage format for backward compatibility
  const collections = apiCollections.map((collection) => ({
    id: collection.id.toString(),
    name: collection.name,
    description: collection.description || undefined,
    image: collection.image || null,
    visibility: collection.isPublic ? ('public' as const) : ('private' as const),
    productIds: collection.items ? collection.items.map((item) => item.id) : [],
    createdAt: new Date(collection.createdAt),
  }));

  // If not authenticated, return empty collections
  if (!session?.user || isSessionPending) {
    return (
      <CollectionsContext.Provider
        value={{
          collections: [],
          createCollection: async () => ({
            id: '',
            name: '',
            visibility: 'private',
            productIds: [],
            createdAt: new Date(),
          }),
          deleteCollection: () => {},
          addToCollection: () => {},
          removeFromCollection: () => {},
          updateCollection: () => {},
          isLoading: isSessionPending,
          error: null,
        }}
      >
        {children}
      </CollectionsContext.Provider>
    );
  }

  const createCollection = async (
    name: string,
    visibility: 'public' | 'private'
  ): Promise<Collection | undefined> => {
    const requestData = transformCollectionToCreateRequest({
      name,
      isPublic: transformVisibilityToIsPublic(visibility),
    });

    try {
      const newCollection = await createMutation.mutateAsync(requestData);
      return {
        id: newCollection.id.toString(),
        name: newCollection.name,
        description: newCollection.description || undefined,
        image: newCollection.image || null,
        visibility: newCollection.isPublic ? ('public' as const) : ('private' as const),
        productIds: [],
        createdAt: new Date(newCollection.createdAt),
      };
    } catch (error) {
      console.error('Failed to create collection:', error);
      return undefined;
    }
  };

  const deleteCollection = (id: string) => {
    deleteMutation.mutate(Number(id), {
      onError: (error) => {
        console.error('Failed to delete collection:', error);
        // React Query will handle the rollback
      },
    });
  };

  const addToCollection = (collectionId: string, productId: string) => {
    return addItemMutation.mutateAsync({
      collectionId: Number(collectionId),
      itemId: productId,
    });
  };

  const removeFromCollection = (collectionId: string, productId: string) => {
    return removeItemMutation.mutateAsync({
      collectionId: Number(collectionId),
      itemId: productId,
    });
  };

  const updateCollection = (
    id: string,
    updates: Partial<Pick<Collection, 'name' | 'visibility' | 'description' | 'image'>> & {
      tags?: string[];
    }
  ) => {
    const requestData = transformCollectionToUpdateRequest({
      name: updates.name,
      description: updates.description,
      image: updates.image,
      isPublic: updates.visibility ? transformVisibilityToIsPublic(updates.visibility) : undefined,
      tags: updates.tags,
    });

    updateMutation.mutate(
      {
        id: Number(id),
        data: requestData,
      },
      {
        onError: (error) => {
          console.error('Failed to update collection:', error);
        },
      }
    );
  };

  return (
    <CollectionsContext.Provider
      value={{
        collections,
        createCollection,
        deleteCollection,
        addToCollection,
        removeFromCollection,
        updateCollection,
        isLoading,
        error,
      }}
    >
      {children}
    </CollectionsContext.Provider>
  );
}

export function useCollections() {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }
  return context;
}
